import { Dropdown } from "antd";
import { CSSProperties, ReactNode } from "react";
import { Colors } from "../../utils/colors";
import CustomCheckbox from "../CustomCheckbox";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";

type DropdownMultiSelectMenuProps<T> = {
  items: T[];
  getId: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  children: ReactNode;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  allLabel?: string;
  overlayStyle?: CSSProperties;
};

// Generic multi-select dropdown: a checkbox per item plus an "All" row that
// clears the selection. Reusable anywhere a filter/picker needs
// "select any number of these,".
export function DropdownMultiSelectMenu<T>(
  props: DropdownMultiSelectMenuProps<T>
) {
  const {
    items,
    getId,
    renderItem,
    selectedIds,
    setSelectedIds,
    children,
    isOpen,
    onOpenChange,
    disabled,
    allLabel = "All",
    overlayStyle,
  } = props;

  const isAllSelected = selectedIds.length === 0;

  const toggleItem = (id: string) => {
    if (isAllSelected) {
      // Every item is implicitly checked (empty selectedIds == "All").
      // Clicking one here means "everything except this one," not "just this one."
      setSelectedIds(items.map(getId).filter((itemId) => itemId !== id));
      return;
    }
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      const next = [...selectedIds, id];
      // Every item now explicitly selected - collapse back to "All" (empty array).
      setSelectedIds(next.length === items.length ? [] : next);
    }
  };

  const styles = {
    panel: {
      width: 240,
      backgroundColor: Colors.WHITE,
      border: `0.5px solid ${Colors.GRAY2}`,
      borderRadius: 10,
      boxShadow: "0 8px 24px rgba(15,15,24,0.12)",
      padding: 6,
      maxHeight: 320,
      overflowY: "auto" as const,
    },
    panelRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "8px 8px",
      borderRadius: 7,
      cursor: "pointer",
    },
  };

  return (
    <Dropdown
      open={disabled ? false : isOpen}
      onOpenChange={(open) => (disabled ? undefined : onOpenChange(open))}
      trigger={["click"]}
      placement="bottom"
      overlayStyle={overlayStyle}
      dropdownRender={() => (
        <div style={styles.panel}>
          <div style={styles.panelRow} onClick={() => setSelectedIds([])}>
            <span className="bodyMedium">{allLabel}</span>
            <CustomCheckbox
              checked={isAllSelected}
              onChange={() => setSelectedIds([])}
              sx={{ padding: 0 }}
            />
          </div>
          <HorizontalDivider altMargin={4} />
          {items.map((item) => {
            const id = getId(item);
            const checked = isAllSelected || selectedIds.includes(id);
            return (
              <div
                key={id}
                style={styles.panelRow}
                onClick={() => toggleItem(id)}
              >
                {renderItem(item)}
                <CustomCheckbox
                  checked={checked}
                  onChange={() => toggleItem(id)}
                  sx={{ padding: 0 }}
                />
              </div>
            );
          })}
        </div>
      )}
    >
      <div style={{ cursor: disabled ? "auto" : "pointer" }}>{children}</div>
    </Dropdown>
  );
}

export default DropdownMultiSelectMenu;
